// Pure timing math, extracted from app.js so it can be unit-tested without a
// browser or the SpessaSynth runtime. These functions own the bug-prone
// measure↔seconds conversions and MIDI tempo parsing (see memory:
// clefai-lessons-learned #5/#9 — time-signature and tempo mistakes).
//
// All functions are pure: time signature is passed as a "n/d" string and tempo
// as a BPM number, so there is no hidden dependency on module-level state.

// Parse a "n/d" time-signature string into beats + beat value.
// Falls back to common time (4/4) on missing or malformed input, matching the
// original app.js behaviour.
export function parseTimeSignature(timeSignature) {
  if (!timeSignature) return { beats: 4, beatValue: 4 };
  const parts = String(timeSignature).split("/");
  return {
    beats: parseInt(parts[0], 10) || 4,
    beatValue: parseInt(parts[1], 10) || 4,
  };
}

// How many quarter notes fill one measure of the given time signature.
// e.g. 4/4 → 4, 6/8 → 3, 3/4 → 3, 2/2 → 4.
export function quarterNotesPerMeasure(timeSignature) {
  const ts = parseTimeSignature(timeSignature);
  return ts.beats * (4 / ts.beatValue);
}

// How often the measure readout is refreshed, in wall seconds. Cosmetic only:
// since the loop ends at end-of-track, nothing audible waits on this poll.
export const LOOP_POLL_SECONDS = 0.05;

// Ticks per measure given the MIDI's ticks-per-quarter-note division.
export function ticksPerMeasure(ticksPerBeat, timeSignature) {
  return ticksPerBeat * quarterNotesPerMeasure(timeSignature);
}

// Parse the first tempo (FF 51) meta event from a raw MIDI buffer and return
// BPM. Reliable and synchronous (no worker round-trip). Defaults to 120 BPM if
// no tempo event is present. Accepts an ArrayBuffer or a typed array.
export function parseMidiTempo(midiBuffer) {
  const src = midiBuffer instanceof Uint8Array ? midiBuffer : new Uint8Array(midiBuffer);
  const numTracks = (src[10] << 8) | src[11];
  let offset = 14;
  for (let t = 0; t < numTracks; t++) {
    while (offset < src.length - 4 && !(src[offset] === 0x4d && src[offset + 1] === 0x54 && src[offset + 2] === 0x72 && src[offset + 3] === 0x6b)) offset++;
    if (offset >= src.length - 4) break;
    const trackLen = (src[offset + 4] << 24) | (src[offset + 5] << 16) | (src[offset + 6] << 8) | src[offset + 7];
    const trackEnd = offset + 8 + trackLen;
    let pos = offset + 8;
    while (pos < trackEnd) {
      // Skip delta time
      while (pos < trackEnd && src[pos] & 0x80) pos++;
      pos++;
      if (pos >= trackEnd) break;
      // Meta event: tempo
      if (src[pos] === 0xff && pos + 1 < trackEnd && src[pos + 1] === 0x51) {
        pos += 2; // skip FF 51
        const len = src[pos]; pos++; // length (should be 3)
        if (len === 3 && pos + 2 < trackEnd) {
          const microsecondsPerBeat = (src[pos] << 16) | (src[pos + 1] << 8) | src[pos + 2];
          return Math.round(60000000 / microsecondsPerBeat);
        }
      }
      // Skip other events
      if (src[pos] === 0xff) { pos += 2; const len = src[pos]; pos++; pos += len; }
      else if (src[pos] >= 0xf0) { pos++; while (pos < trackEnd && src[pos] !== 0xf7) pos++; pos++; }
      else if (src[pos] >= 0xc0 && src[pos] < 0xe0) pos += 2;
      else if (src[pos] >= 0x80) pos += 3;
      else pos++;
    }
    offset = trackEnd;
  }
  return 120; // default
}

// Write a MIDI variable-length quantity.
export function writeVarLen(value) {
  let v = Math.max(0, Math.round(value));
  if (v < 0x80) return [v];
  const bytes = [v & 0x7f];
  v >>= 7;
  while (v > 0) {
    bytes.unshift((v & 0x7f) | 0x80);
    v >>= 7;
  }
  return bytes;
}

// ─── Measure table ──────────────────────────────────────────
//
// Until now every measure was assumed to be the same length: one time signature
// per piece, `measure * secondsPerMeasure`. That breaks on two real cases in the
// repertoire:
//
//   - Winter Wonderland notates 4/4 for three bars and then 2/2 (swing). Both are
//     four quarters, so the grid survives, but the metronome should click two per
//     bar from bar 4, not four.
//   - The Christmas medley is 4/4 for 32 bars and then 3/4 for 42, and it opens
//     with a one-quarter pickup (`\partial 4` in the LilyPond source). Bars there
//     are genuinely not the same length, so arithmetic cannot find a barline.
//
// So measures are a TABLE, read from the MIDI's own time-signature meta events,
// and everything that needs a barline looks it up. A piece with one time
// signature produces a uniform table and behaves exactly as before.
//
// The pickup: a segment whose span is not a whole number of measures has the
// remainder at its START — that is what an anacrusis is. Bar 1 is the first FULL
// bar, matching how LilyPond numbers a score with \partial, so the numbers in the
// app are the numbers on the player's sheet. The pickup is reachable as measure 0.
export function buildMeasureTable(midiBuffer) {
  const src = midiBuffer instanceof Uint8Array ? midiBuffer : new Uint8Array(midiBuffer);
  const division = (src[12] << 8) | src[13];
  const numTracks = (src[10] << 8) | src[11];

  const changes = new Map(); // tick → "n/d", deduplicated across tracks
  let lastTick = 0;
  let offset = 14;
  for (let t = 0; t < numTracks; t++) {
    while (offset < src.length - 4 && !(src[offset] === 0x4d && src[offset + 1] === 0x54 && src[offset + 2] === 0x72 && src[offset + 3] === 0x6b)) offset++;
    if (offset >= src.length - 4) break;
    const trackLen = (src[offset + 4] << 24) | (src[offset + 5] << 16) | (src[offset + 6] << 8) | src[offset + 7];
    const trackEnd = offset + 8 + trackLen;
    let pos = offset + 8;
    let tick = 0;
    let running = 0;
    while (pos < trackEnd) {
      let delta = 0;
      while (pos < trackEnd && src[pos] & 0x80) { delta = (delta << 7) | (src[pos] & 0x7f); pos++; }
      delta = (delta << 7) | src[pos]; pos++;
      tick += delta;
      if (tick > lastTick) lastTick = tick;
      if (pos >= trackEnd) break;
      let status = src[pos];
      if (status < 0x80) status = running;
      else { pos++; if (status < 0xf0) running = status; }
      if (status === 0xff) {
        const type = src[pos]; pos++;
        let len = 0;
        while (pos < trackEnd && src[pos] & 0x80) { len = (len << 7) | (src[pos] & 0x7f); pos++; }
        len = (len << 7) | src[pos]; pos++;
        if (type === 0x58 && len >= 2) changes.set(tick, `${src[pos]}/${Math.pow(2, src[pos + 1])}`);
        pos += len;
      } else if (status === 0xf0 || status === 0xf7) {
        let len = 0;
        while (pos < trackEnd && src[pos] & 0x80) { len = (len << 7) | (src[pos] & 0x7f); pos++; }
        len = (len << 7) | src[pos]; pos++;
        pos += len;
      } else {
        const kind = status & 0xf0;
        pos += kind === 0xc0 || kind === 0xd0 ? 1 : 2;
      }
    }
    offset = trackEnd;
  }

  if (!changes.has(0)) changes.set(0, "4/4"); // MIDI default when none is written
  const segments = [...changes.entries()].sort((a, b) => a[0] - b[0]);

  const measures = [];
  let pickupTicks = 0;
  for (let i = 0; i < segments.length; i++) {
    const [startTick, signature] = segments[i];
    const endTick = i + 1 < segments.length ? segments[i + 1][0] : lastTick;
    const ts = parseTimeSignature(signature);
    const perMeasure = ticksPerMeasure(division, signature);
    let cursor = startTick;
    const span = endTick - startTick;
    // A leftover only makes sense at the very beginning, as a pickup. Anywhere
    // else it would mean the file disagrees with itself about where barlines are;
    // the leftover is then carried as a short measure rather than silently
    // shifting every bar number after it.
    const leftover = span % perMeasure;
    if (leftover > 0 && i === 0) {
      pickupTicks = leftover;
      measures.push({ number: 0, startTick: cursor, ticks: leftover, beats: ts.beats, beatValue: ts.beatValue, pickup: true });
      cursor += leftover;
    }
    const full = Math.floor((endTick - cursor) / perMeasure);
    for (let m = 0; m < full; m++) {
      measures.push({ number: 0, startTick: cursor, ticks: perMeasure, beats: ts.beats, beatValue: ts.beatValue, pickup: false });
      cursor += perMeasure;
    }
    if (cursor < endTick) {
      measures.push({ number: 0, startTick: cursor, ticks: endTick - cursor, beats: ts.beats, beatValue: ts.beatValue, pickup: false, short: true });
    }
  }

  // Number the full measures 1..n; the pickup keeps 0.
  let n = 0;
  for (const m of measures) m.number = m.pickup ? 0 : ++n;

  return { division, pickupTicks, lastTick, totalMeasures: n, measures };
}

// Start tick of a 1-based measure. Measure 0 is the pickup when there is one.
// One past the last measure returns the end of the piece, so a loop can ask for
// the barline that closes its last measure.
export function measureStartTick(table, measure) {
  const found = table.measures.find((m) => m.number === measure && !(measure === 0 && !m.pickup));
  if (found) return found.startTick;
  if (measure > table.totalMeasures) {
    const last = table.measures[table.measures.length - 1];
    return last.startTick + last.ticks;
  }
  return 0;
}

// Where a loop starting at `fromMeasure` begins in the source MIDI.
//
// Bar 1 means "from the top", and from the top includes the pickup: a piece that
// opens with an anacrusis begins there, and nobody rehearsing bar 1 wants it
// dropped. Bar numbering is untouched either way — the pickup is measure 0, so
// bar 1 stays bar 1 on the player's sheet.
export function loopStartTick(table, fromMeasure) {
  return fromMeasure === 1 ? 0 : measureStartTick(table, fromMeasure);
}

// Where everything sits in the excerpt for a loop over `fromMeasure..toMeasure`.
//
// The excerpt opens with one count-off bar in the meter of the start measure
// (`countInTicks`). The piece's tick `fromTick` lands at `musicStart`, so an
// excerpt tick is `pieceTick - fromTick + musicStart`.
//
// Usually the music starts right after the count-off. From the top of a piece
// with a pickup it starts earlier: the pickup is played over the last beats of
// the count-off, the way a conductor counts it in, so bar 1 still lands on the
// barline that ends the count-off.
export function excerptLayout(table, fromMeasure, toMeasure) {
  const first = table.measures.find((m) => m.number === fromMeasure) || table.measures[0];
  const fromTick = loopStartTick(table, fromMeasure);
  const toTick = measureStartTick(table, toMeasure + 1);
  const countInTicks = first.ticks;
  const pickupTicks = fromMeasure === 1 ? Math.min(table.pickupTicks, countInTicks) : 0;
  const musicStart = countInTicks - pickupTicks;
  return { first, fromTick, toTick, countInTicks, musicStart, endTick: toTick - fromTick + musicStart };
}

// The measure a tick falls in (1-based; 0 for the pickup).
export function measureAtTick(table, tick) {
  let result = table.measures.length ? table.measures[0].number : 1;
  for (const m of table.measures) {
    if (tick < m.startTick) break;
    result = m.number;
  }
  return result;
}

// Seconds per tick at a constant tempo. Every piece in the repertoire has exactly
// one tempo event; parseMidiTempo reads it and this converts with it.
export function secondsPerTick(division, tempoBpm) {
  return 60 / (tempoBpm * division);
}

// Default values for the controllers the preamble carries. Anything still at its
// default is not worth writing.
const CC_DEFAULTS = { 7: 100, 10: 64, 11: 127, 91: 40, 93: 0 };
const CARRIED_CCS = [7, 10, 11, 64, 91, 93];

// Cut one track down to the tick range [fromTick, toTick) and move it to `shift`.
//
// This is what makes a loop its own MIDI instead of a seek into a long piece.
// The sequencer then plays from 0 to end-of-track, so the loop END needs no
// timing decision at all: the file simply stops there.
//
// The hard part is not the cutting, it is what has to come WITH it. Everything
// the piece established before the cut — instrument, volume, expression, pan,
// pitch bend, tempo, time signature — lives in events that are now gone, so the
// state is accumulated while walking to `fromTick` and written as a preamble at
// tick 0. Skipping that is tempting because 8 of 9 files in the repertoire put
// all of it at tick 0 anyway; Feuerwerk does not (its whole preamble sits at tick
// 1344), which is exactly how a shortcut here would slip through.
//
// Notes that start inside the range and end after it would hang, so every channel
// the track touched gets an all-notes-off at the end.
export function extractTrackRange(source, trackStart, trackEnd, fromTick, toTick, shift) {
  const src = source instanceof Uint8Array ? source : new Uint8Array(source);

  const programs = new Map();   // channel → program
  const controllers = new Map(); // channel → Map(cc → value)
  const bends = new Map();      // channel → [lsb, msb]
  const channels = new Set();
  let tempo = null;             // raw 3 bytes
  let timeSignature = null;     // raw 4 bytes
  let trackName = null;

  const body = [];              // events from fromTick on, as {tick, bytes}
  let pos = trackStart;
  let tick = 0;
  let running = 0;

  while (pos < trackEnd) {
    let delta = 0;
    while (pos < trackEnd && src[pos] & 0x80) { delta = (delta << 7) | (src[pos] & 0x7f); pos++; }
    delta = (delta << 7) | src[pos]; pos++;
    tick += delta;
    if (pos >= trackEnd) break;

    let status = src[pos];
    const hadStatus = status >= 0x80;
    if (!hadStatus) status = running;
    else { pos++; if (status < 0xf0) running = status; }

    if (status === 0xff) {
      const type = src[pos]; pos++;
      let len = 0;
      while (pos < trackEnd && src[pos] & 0x80) { len = (len << 7) | (src[pos] & 0x7f); pos++; }
      len = (len << 7) | src[pos]; pos++;
      const data = src.slice(pos, pos + len);
      pos += len;
      if (type === 0x51) tempo = data;
      else if (type === 0x58) timeSignature = data;
      else if (type === 0x03 && trackName === null) trackName = data;
      // End of track is re-emitted by the writer, never copied.
      if (type !== 0x2f && tick >= fromTick && tick < toTick) {
        body.push({ tick, bytes: [0xff, type, ...writeVarLen(len), ...data] });
      }
      continue;
    }

    if (status === 0xf0 || status === 0xf7) {
      let len = 0;
      while (pos < trackEnd && src[pos] & 0x80) { len = (len << 7) | (src[pos] & 0x7f); pos++; }
      len = (len << 7) | src[pos]; pos++;
      pos += len;
      continue; // SysEx is not carried; nothing in the repertoire uses it
    }

    const kind = status & 0xf0;
    const channel = status & 0x0f;
    const size = kind === 0xc0 || kind === 0xd0 ? 1 : 2;
    const d1 = src[pos];
    const d2 = size === 2 ? src[pos + 1] : 0;
    pos += size;
    channels.add(channel);

    // Accumulate state whether the event is before or inside the range: a value
    // set inside the range is already in `body`, and the preamble only writes
    // what it knew BEFORE fromTick, so track that separately.
    if (tick < fromTick) {
      if (kind === 0xc0) programs.set(channel, d1);
      else if (kind === 0xb0) {
        if (!controllers.has(channel)) controllers.set(channel, new Map());
        controllers.get(channel).set(d1, d2);
      } else if (kind === 0xe0) bends.set(channel, [d1, d2]);
    }

    if (tick >= fromTick && tick < toTick) {
      body.push({ tick, bytes: size === 2 ? [status, d1, d2] : [status, d1] });
    }
  }

  // ─ Write the new track ─
  const out = [];
  const push = (deltaTicks, bytes) => { out.push(...writeVarLen(deltaTicks), ...bytes); };
  let cursor = 0;

  if (trackName) push(0, [0xff, 0x03, ...writeVarLen(trackName.length), ...trackName]);
  if (tempo) push(0, [0xff, 0x51, 0x03, ...tempo]);
  if (timeSignature) push(0, [0xff, 0x58, ...writeVarLen(timeSignature.length), ...timeSignature]);

  for (const channel of [...channels].sort((a, b) => a - b)) {
    const cc = controllers.get(channel);
    if (cc) {
      for (const number of CARRIED_CCS) {
        if (!cc.has(number)) continue;
        const value = cc.get(number);
        if (CC_DEFAULTS[number] !== undefined && value === CC_DEFAULTS[number]) continue;
        push(0, [0xb0 | channel, number, value]);
      }
    }
    if (programs.has(channel)) push(0, [0xc0 | channel, programs.get(channel)]);
    const bend = bends.get(channel);
    if (bend && !(bend[0] === 0 && bend[1] === 64)) push(0, [0xe0 | channel, bend[0], bend[1]]);
  }

  // The preamble all sits at tick 0; the body starts at `shift`.
  for (const event of body) {
    const at = event.tick - fromTick + shift;
    push(at - cursor, event.bytes);
    cursor = at;
  }

  const endTick = toTick - fromTick + shift;
  for (const channel of [...channels].sort((a, b) => a - b)) {
    push(endTick - cursor, [0xb0 | channel, 123, 0]); // all notes off
    cursor = endTick;
  }
  push(endTick - cursor, [0xff, 0x2f, 0x00]);

  return new Uint8Array(out);
}
